import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { MeterMakeForSelect, MeterModel, MeterModelSaveDto } from 'src/app/models/meter.model';
import { PermissionsEnum } from 'src/app/models/role.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { MeterModelService } from 'src/app/services/meter-model.service';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';
import { PermissionsService } from 'shared-lib';
import { MeterService } from 'src/app/services/meter.service';

@Component({
  selector: 'crud-meter-model',
  templateUrl: './crud-meter-model.component.html',
  styleUrls: ['./crud-meter-model.component.scss']
})
export class CrudMeterModelComponent implements OnInit{
  @Input() searchText!: string;
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;
  @ViewChild('closeDeleteModal') closeDeleteModal: ElementRef;
  @ViewChild('closeAddEditModal') closeAddEditModal: ElementRef;

  public PermissionsEnum = PermissionsEnum;

  public currentPage: number = 1;
  public pageSize: number = 5;
  public count: number = 0;

  meterModels: MeterModel[] = [];
  selectedMeterModel: MeterModel = new MeterModel();
  isEditMode: boolean = false;
  meterTypes: MeterMakeForSelect[] = [];
  selectedMeterType: MeterMakeForSelect | undefined = undefined;

  actionLabel$!: Observable<string>;
  messages$: Observable<{ [key: string]: string }>;

  constructor(
    private meterModelService: MeterModelService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    public meterService: MeterService
  ) {}

  ngOnInit(): void {
    this.getMeterTypesForSelect();
     if (this.utilityId != 0) {
      this.reloadTable();
    }
  }

  reloadTable(searchText?: string, page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllMeterModels(dataIn);
  }

  private getAllMeterModels(dataIn: any) {
    this.meterModelService.getAll(dataIn).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.meterModels = response.data.data;
          this.count = response.data.count;
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(
            messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]
          );
        });
      },
    });
  }

  filter(){
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllMeterModels(dataIn);
  }

  private updateActionLabel(): void {
    const key = this.isEditMode
      ? TranslationUtils.EDIT_KEY
      : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  addNew() {
    this.isEditMode = false;
    this.selectedMeterModel = new MeterModel();
    this.selectedMeterType = undefined;
    this.updateActionLabel();
  }

  selectMeterModel(meterModel: MeterModel){
    this.isEditMode = true;
    this.selectedMeterModel = { ...meterModel };
    this.selectedMeterType = this.meterTypes.find(x => x.id === meterModel.meterBrand.id);
    this.updateActionLabel();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable(this.searchText);
  }

  onModalFade(){
    this.selectedMeterModel = new MeterModel();
    this.selectedMeterType = undefined;
  }

  save(){
    if (this.selectedMeterType === undefined)
      this.toastr.warning('Please select a Meter Type');
    else {

      const dataIn = new MeterModelSaveDto({
        id: this.selectedMeterModel.id,
        name: this.selectedMeterModel.name,
        meterBrandId: this.selectedMeterType!.id
      });

      this.meterModelService.save(dataIn).subscribe({
        next: (response) => {
          if (response.status === 200 || response.status === 'OK') {
            this.closeAddEditModal?.nativeElement?.click();
            this.toastr.success(response.message);
            if (this.selectedMeterModel.id === null) {
              this.pageChange(1);
              return;
            }
            this.reloadTable(this.searchText);
          } else {
            this.toastr.error(response.message);
          }
        },
        error: (_) => {
          this.messages$.subscribe((messages) => {
            this.toastr.error(
             messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]
            );
          });
        },
      });
    }
  }

  deleteMeterModel(){
     this.meterModelService.delete(this.selectedMeterModel.id!).subscribe({
      next: (response) => {
        this.closeDeleteModal?.nativeElement?.click();
        if (response?.status === 200 || response?.status === 'OK') {
          this.pageChange(1);
          this.toastr.success(response?.message);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(
            messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]
          );
        });
      },
    });
  }

  getMeterTypesForSelect() {
    this.meterService.getAllMeterTypesForSelect().subscribe({
      next: (response: any) => {
        var responseData = response?.body;
        if (responseData.status === 200 || responseData.status === 'OK') {
          this.meterTypes = responseData?.data.map((x: any) => {
            return {
              id: x.id,
              name: x.name,
            } as MeterMakeForSelect
          })
        }
      },
      error: (_: any) => this.toastr.error('An error ocurred while fetching meter types.'),
    });
  }
}
